import { Fragment, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useKanbanDnd } from '../../lib/dnd';
import { CandidateStage } from '@masamune/shared';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { toast } from '../../stores/toast.store';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon } from '../../components/ui/Icon';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { Chip, statusTone } from '../../components/ui/Chip';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { Avatar } from '../../components/ui/Avatar';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { EmptyState } from '../../components/ui/EmptyState';
import { LifecycleStepper, NextActionCard, type LifecycleStep, type LifecycleAction } from '../../components/ui/LifecycleStepper';
import { fmtEur, fmtDate, fmtDateShort } from '../../lib/utils';

interface CourseFull {
  id: string; name: string; type: 'FORMATEMP' | 'FONDIMPRESA' | 'INTERNO'; status: string; mode: string;
  partnerName: string | null; startDate: string; endDate: string; hoursTotal: number;
  enrolledCount: number; revenue: string; cost?: number; description: string | null;
  objectives: string | null;
  client: { id: string; legalName: string } | null;
  teachers: Array<{ id: string; user: { firstName: string; lastName: string; initials: string } | null; supplier: any }>;
  lessons: Array<{ id: string; date: string; startTime: string; endTime: string; topic: string; room: string | null }>;
  enrollments: Array<{ id: string; candidate: { id: string; firstName: string; lastName: string; city: string | null; stage: string } }>;
  documents: any[];
  assessments: any[];
}

type Tab = 'overview' | 'selezione' | 'aula' | 'persone' | 'esiti' | 'amministrazione';

export function CourseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const openModal = useModalStore((s) => s.open);
  const user = useAuthStore((s) => s.user);
  const showMargin = !!user?.role.canSeeMargin;
  const [tab, setTab] = useState<Tab>('overview');
  // sotto-vista del tab Aula: registro presenze (default) o calendario lezioni
  const [aulaView, setAulaView] = useState<'presenze' | 'calendario'>('presenze');

  const { data: c, isLoading, isError } = useQuery({
    queryKey: ['course', id],
    queryFn: () => api.get<CourseFull>(`/courses/${id}`),
    enabled: !!id,
    retry: false,
  });

  const { data: attendance } = useQuery({
    queryKey: ['attendance-matrix', id],
    queryFn: () => api.get<{ rows: Array<{ attendancePct: number }> }>(`/courses/${id}/attendance-matrix`),
    enabled: !!id,
  });

  const qc = useQueryClient();
  const patchStatus = useMutation({
    mutationFn: (status: string) => api.patch(`/courses/${id}`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['course', id] }); qc.invalidateQueries({ queryKey: ['courses'] }); },
  });

  if (!id) return <Navigate to="/talent" replace />;
  if (isError) return (
    <div className="page">
      <Breadcrumb items={[{ label: 'Talent', to: '/talent' }, { label: c?.name ?? 'Corso' }]} />
      <div className="max-w-[480px] mx-auto py-20 text-center">
        <h1 className="t-h1">Corso non trovato</h1>
        <div className="mute mt-2">L'ID richiesto non esiste.</div>
      </div>
    </div>
  );
  if (isLoading || !c) return <DetailSkeleton header="square" />;

  const avgAttendance = attendance?.rows.length
    ? Math.round(attendance.rows.reduce((a, r) => a + r.attendancePct, 0) / attendance.rows.length)
    : null;

  // Calcolo REALE dalle lezioni pianificate: done = lezioni con data passata.
  const totalLessons = c.lessons.length;
  const lessonsDone = c.lessons.filter((l) => new Date(l.date) < new Date()).length;

  // NB: il margine completo (€/% + ripartizione costi) vive nel BudgetTab di
  // Amministrazione, che lo ricalcola dal proprio endpoint. La striscia header
  // mostra solo lo stato sintetico del corso.

  // Prossima lezione = la più imminente tra quelle ancora da svolgere.
  const nextLesson = c.lessons
    .filter((l) => new Date(l.date) >= new Date())
    .sort((a, b) => +new Date(a.date) - +new Date(b.date))[0] ?? null;
  // Discenti pronti al placement = iscritti nello stage PRONTO_PER_PLACEMENT.
  const readyForPlacement = c.enrollments.filter(
    (e) => e.candidate.stage === CandidateStage.PRONTO_PER_PLACEMENT,
  ).length;

  const isFondimpresa = c.type === 'FONDIMPRESA';
  // Stepper condiviso a 6 step: SOLO gli stati reali del corso. Il click su uno
  // step fa avanzare/regredire lo `status` (stessa patchStatus di prima).
  type CourseStep = LifecycleStep & { status: string };
  const lifecycleSteps: CourseStep[] = [
    { key: 'ISCRIZIONI',  label: 'Iscrizioni',  icon: 'users',    status: 'ISCRIZIONI' },
    { key: 'SELEZIONE',   label: 'Selezione',   icon: 'filter',   status: 'SELEZIONE' },
    { key: 'AULA',        label: 'Aula',        icon: 'calendar', status: 'AULA' },
    { key: 'VALUTAZIONE', label: 'Valutazione', icon: 'chart',    status: 'VALUTAZIONE' },
    isFondimpresa
      ? { key: 'RENDICONTAZIONE', label: 'Rendicontazione', icon: 'file', status: 'RENDICONTAZIONE' }
      : { key: 'PLACEMENT',       label: 'Placement',       icon: 'send', status: 'PLACEMENT' },
    { key: 'CHIUSO', label: 'Chiuso', icon: 'check', status: 'CHIUSO' },
  ];
  let currentStepIdx = lifecycleSteps.findIndex((s) => s.status === c.status);
  if (currentStepIdx < 0) currentStepIdx = c.status === 'PLACEMENT' || c.status === 'RENDICONTAZIONE' ? 4 : 0;

  // Prossima azione suggerita in base allo status corrente. La sua CTA è anche
  // l'unico bottone primary dell'header.
  const nextAction: LifecycleAction | null = (() => {
    switch (c.status) {
      case 'ISCRIZIONI':
        return {
          title: 'Aggiungi candidati al funnel',
          body: `Il corso è in fase iscrizioni: porta i candidati nel funnel di ${isFondimpresa ? 'iscrizione' : 'selezione'}.`,
          cta: 'Aggiungi candidati', icon: 'users', tone: 'primary',
          onClick: () => setTab('selezione'),
        };
      case 'AULA':
        return {
          title: 'Firma le presenze di oggi',
          body: 'Il corso è in aula: registra le presenze della lezione di oggi nel registro.',
          cta: 'Apri registro', icon: 'check', tone: 'primary',
          onClick: () => { setTab('aula'); setAulaView('presenze'); },
        };
      case 'VALUTAZIONE':
        return {
          title: 'Registra le prove',
          body: 'Il corso è in valutazione: registra le prove e i punteggi dei discenti.',
          cta: 'Apri Esiti', icon: 'chart', tone: 'primary',
          onClick: () => setTab('esiti'),
        };
      case 'RENDICONTAZIONE':
        return {
          title: 'Carica i documenti mancanti',
          body: 'Completa la checklist documentale per inviare il rendiconto a Fondimpresa.',
          cta: 'Apri Amministrazione', icon: 'upload', tone: 'warn',
          onClick: () => setTab('amministrazione'),
        };
      default:
        return null;
    }
  })();

  const tabs: { k: Tab; l: string }[] = [
    { k: 'overview',        l: 'Overview' },
    { k: 'selezione',       l: isFondimpresa ? 'Iscrizioni' : 'Selezione' },
    { k: 'aula',            l: 'Aula' },
    { k: 'persone',         l: 'Persone' },
    { k: 'esiti',           l: 'Esiti' },
    { k: 'amministrazione', l: 'Amministrazione' },
  ];

  return (
    <div className="page">
      <Breadcrumb items={[{ label: 'Talent', to: '/talent' }, { label: c?.name ?? 'Corso' }]} />

      {/* Header editoriale: titolo leggero e grande, hairline aurora dal ::after */}
      <div className="page-ed-hd flex items-start justify-between gap-4">
        <div className="min-w-0 max-w-[760px]">
          {/* L1 protagonista: il nome (peso leggero, grande), tronca se lungo */}
          <h1 className="ed-title truncate">{c.name}</h1>
          {/* L3 meta-indice: tipo·modalità·ore·iscritti su UNA riga mute (niente UUID: non lo legge nessuno) */}
          <div className="detail-meta mt-2 truncate">
            <span>{c.type}</span>
            <span className="mute-2">·</span>
            <span className="lowercase">{c.mode.replace('_', ' ')}</span>
            <span className="mute-2">·</span>
            <span><span className="v">{c.hoursTotal}</span>h</span>
            <span className="mute-2">·</span>
            <span><span className="v">{c.enrolledCount}</span> iscritti</span>
          </div>
          {/* L4 più arretrata: partner + cliente, label mute, valori --text peso 400 */}
          <div className="detail-meta mt-1 truncate">
            {c.partnerName && <span><span className="mute-2 mr-1.5">Partner</span><span className="v">{c.partnerName}</span></span>}
            {c.partnerName && c.client && <span className="mute-2">·</span>}
            {c.client && <span><span className="mute-2 mr-1.5">Azienda</span><span className="v">{c.client.legalName}</span></span>}
          </div>
          {/* Stato: riga dedicata SOTTO, unico accento-colore dell'header */}
          <div className="flex gap-1.5 mt-2.5 items-center">
            <Chip tone={statusTone(c.status)}>{c.status.replace('_', ' ')}</Chip>
          </div>
        </div>
        <div className="flex gap-2 pb-1">
          {nextAction && (
            <button className="btn btn-primary" onClick={nextAction.onClick} disabled={nextAction.loading}>
              <Icon name={nextAction.icon} size={12} /> {nextAction.cta}
            </button>
          )}
          <ActionsMenu
            variant="ghost"
            items={[
              { key: 'print', label: 'Stampa scheda', icon: 'export', onClick: () => { if (typeof window !== 'undefined') window.print(); } },
              { key: 'upload', label: 'Carica documento', icon: 'upload', onClick: () => openModal('uploadDoc', { scope: 'course', scopeId: c.id }) },
            ]}
          />
        </div>
      </div>

      {/* Numeri NUDI sul foglio, separati da hairline: niente scatole né icone */}
      <div className="stat-band">
        <div>
          <div className="st-label">Fase</div>
          <div className="st-value">{lifecycleSteps[currentStepIdx]?.label ?? c.status.replace(/_/g, ' ')}</div>
          <div className="st-delta">{lessonsDone}/{totalLessons} lezioni</div>
        </div>
        <div>
          <div className="st-label">Presenze medie</div>
          <div className="st-value">
            {avgAttendance != null
              ? <><AnimatedNumber value={avgAttendance} /><span className="u">%</span></>
              : <span className="mute-2">—</span>}
          </div>
          {avgAttendance == null && <div className="st-delta">no dati</div>}
        </div>
        <div>
          <div className="st-label">Prossima lezione</div>
          <div className="st-value">{nextLesson ? fmtDate(nextLesson.date) : <span className="mute-2">—</span>}</div>
          <div className="st-delta">{nextLesson?.topic ?? 'nessuna in agenda'}</div>
        </div>
        <div>
          <div className="st-label">Pronti al placement</div>
          <div className="st-value"><AnimatedNumber value={readyForPlacement} /></div>
          <div className="st-delta">discenti</div>
        </div>
      </div>

      <LifecycleStepper
        steps={lifecycleSteps}
        currentIdx={currentStepIdx}
        onStepClick={(_i, step) => {
          const s = (step as CourseStep).status;
          if (!patchStatus.isPending && s !== c.status) patchStatus.mutate(s);
        }}
      />

      {nextAction && <div className="mb-6"><NextActionCard action={nextAction} /></div>}

      <div className="page-tabs">
        {tabs.map((t) => (
          <div key={t.k} className="page-tab" aria-selected={tab === t.k} onClick={() => setTab(t.k)}>
            {t.l}
          </div>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab c={c} avgAttendance={avgAttendance} />}

      {/* Selezione (Formatemp) / Iscrizioni (Fondimpresa) = funnel per-corso */}
      {tab === 'selezione' && <CoursePipelineTab courseId={c.id} />}

      {/* Aula: registro presenze + calendario lezioni in seg-control */}
      {tab === 'aula' && (
        <div className="space-y-4">
          <div className="seg-control" style={{ width: 'fit-content' }}>
            <button aria-pressed={aulaView === 'presenze'} onClick={() => setAulaView('presenze')}>Registro presenze</button>
            <button aria-pressed={aulaView === 'calendario'} onClick={() => setAulaView('calendario')}>Calendario</button>
          </div>
          {aulaView === 'presenze' ? <AttendanceTab courseId={c.id} /> : <LessonsTab c={c} />}
        </div>
      )}

      {/* Persone: discenti + docenti impilati */}
      {tab === 'persone' && (
        <div className="col gap-6">
          <StudentsTab c={c} />
          <TeachersTab courseId={c.id} />
        </div>
      )}

      {/* Esiti: valutazioni + placement (solo Formatemp) */}
      {tab === 'esiti' && (
        <div className="col gap-6">
          <AssessmentTab courseId={c.id} enrollments={c.enrollments} />
          {!isFondimpresa && <PlacementTab courseId={c.id} />}
        </div>
      )}

      {/* Amministrazione: rendicontazione (Fondimpresa) sopra, poi documenti, budget e log */}
      {tab === 'amministrazione' && (
        <div className="col gap-6">
          {isFondimpresa && <ReportingTab courseId={c.id} />}
          <DocumentsTab courseId={c.id} />
          <BudgetTab courseId={c.id} showMargin={showMargin} />
          <ActivityTab courseId={c.id} />
        </div>
      )}
    </div>
  );
}

// =====================================================================
// OVERVIEW
// =====================================================================
function OverviewTab({ c, avgAttendance }: { c: CourseFull; avgAttendance: number | null }) {
  const upcoming = c.lessons.filter((l) => new Date(l.date) >= new Date());
  return (
    <div className="grid gap-6" style={{ gridTemplateColumns: '2fr 1fr' }}>
      <div className="min-w-0">
        {/* Anagrafica: coppie chiave-valore NUDE sul foglio (hairline, niente card) */}
        <div className="data-hd">
          <span className="t-micro">Informazioni corso</span>
        </div>
        <div className="ed-kv">
          <div className="ed-kv-row"><span className="k">Tipo finanziamento</span><span className="v">{c.type === 'FORMATEMP' ? 'Formatemp · somministrazione' : c.type === 'FONDIMPRESA' ? 'Fondimpresa · interno azienda' : 'Interno'}</span></div>
          <div className="ed-kv-row"><span className="k">Partner</span><span className="v">{c.partnerName ?? '—'}</span></div>
          <div className="ed-kv-row"><span className="k">Azienda cliente</span><span className="v">{c.client?.legalName ?? '—'}</span></div>
          <div className="ed-kv-row"><span className="k">Modalità</span><span className="v">{c.mode.replace('_', ' ')}</span></div>
          <div className="ed-kv-row"><span className="k">Periodo</span><span className="v mono">{fmtDate(c.startDate)} → {fmtDate(c.endDate)}</span></div>
          <div className="ed-kv-row"><span className="k">Ore totali</span><span className="v">{c.hoursTotal}h</span></div>
          {c.objectives && <div className="ed-kv-row"><span className="k">Obiettivi</span><span className="v leading-relaxed">{c.objectives}</span></div>}
        </div>

        <div className="ed-divider" />

        {/* Tabella full-bleed sul foglio: kicker micro + conteggio mono */}
        <div className="ed-section">
          <div className="data-hd">
            <span className="t-micro">Prossime lezioni</span>
            <span className="mono mute text-[12px]">{upcoming.length}</span>
          </div>
          <table className="tbl tbl--ed">
            <thead><tr><th>Data</th><th>Orario</th><th>Argomento</th><th>Aula</th></tr></thead>
            <tbody>
              {upcoming.slice(0, 5).map((l) => (
                <tr key={l.id}>
                  <td className="mono mute text-[12px]">{fmtDate(l.date)}</td>
                  <td className="mono text-[12px]">{l.startTime} — {l.endTime}</td>
                  <td className="font-semibold">{l.topic}</td>
                  <td className="mute">{l.room ?? '—'}</td>
                </tr>
              ))}
              {!upcoming.length && (
                <tr><td colSpan={4} className="text-center py-6 mute text-[13px]">Nessuna lezione futura</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Colonna laterale: kicker-blocks senza bordi, separati da hairline */}
      <div className="min-w-0">
        <div>
          <div className="data-hd">
            <span className="t-micro">Health</span>
          </div>
          <div className="space-y-2 text-[13px]">
            <HealthRow label="Iscrizioni" value={c.enrollments.length > 0 ? `${c.enrollments.length} iscritti` : 'Nessun iscritto'} tone={c.enrollments.length > 0 ? 'ok' : 'warn'} />
            <HealthRow
              label="Presenze"
              value={avgAttendance != null ? `${avgAttendance}% media` : 'No dati'}
              tone={avgAttendance == null ? 'ghost' : avgAttendance >= 80 ? 'ok' : avgAttendance >= 60 ? 'warn' : 'alert'}
            />
            <HealthRow label="Documenti" value={`${c.documents?.length ?? 0} caricati`} tone="ghost" />
            <HealthRow label="Valutazioni" value={`${c.assessments?.length ?? 0} registrate`} tone={c.assessments?.length ? 'ok' : 'warn'} />
          </div>
        </div>

        <div className="ed-divider" />

        <div>
          <div className="data-hd">
            <span className="t-micro">Docenti</span>
          </div>
          <div className="space-y-2">
            {c.teachers.map((t) => (
              t.user && (
                <div key={t.id} className="flex items-center gap-2.5">
                  <Avatar initials={t.user.initials} />
                  <span className="font-semibold text-[13px]">{t.user.firstName} {t.user.lastName}</span>
                </div>
              )
            ))}
            {!c.teachers.length && <div className="py-3 text-center text-[13px] mute">Nessun docente assegnato</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function HealthRow({ label, value, tone }: { label: string; value: string; tone: 'ok' | 'warn' | 'alert' | 'ghost' }) {
  return (
    <div className="flex items-center justify-between">
      <span className="mute">{label}</span>
      <Chip tone={tone}>{value}</Chip>
    </div>
  );
}

// =====================================================================
// LEZIONI
// =====================================================================
function LessonsTab({ c }: { c: CourseFull }) {
  return (
    // Tabella full-bleed sul foglio: kicker micro + conteggio + CTA, niente card
    <div className="ed-section">
      <div className="data-hd">
        <span className="t-micro">Calendario lezioni</span>
        <div className="flex items-center gap-3">
          <span className="mono mute text-[12px]">{c.lessons.length}</span>
          <button className="btn btn-sm btn-primary" onClick={() => useModalStore.getState().open('newLesson', { courseId: c.id })}>
            <Icon name="plus" size={12} /> Nuova lezione
          </button>
        </div>
      </div>
      <table className="tbl tbl--ed">
        <thead><tr><th>Data</th><th>Orario</th><th>Argomento</th><th>Aula</th><th>Stato</th></tr></thead>
        <tbody>
          {c.lessons.map((l) => {
            const isPast = new Date(l.date) < new Date();
            return (
              <tr key={l.id}>
                <td className="mono">{fmtDate(l.date)}</td>
                <td className="mono">{l.startTime} — {l.endTime}</td>
                <td className="font-semibold">{l.topic}</td>
                <td className="mute">{l.room ?? '—'}</td>
                <td><Chip tone={isPast ? 'ok' : 'info'}>{isPast ? 'Svolta' : 'Pianificata'}</Chip></td>
              </tr>
            );
          })}
          {!c.lessons.length && <tr><td colSpan={5} className="text-center py-10 mute text-[13px]">Nessuna lezione</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// =====================================================================
// PRESENZE (matrice firmabile)
// =====================================================================
interface AttendanceMatrix {
  lessons: Array<{ id: string; date: string; topic: string; startTime: string; endTime: string }>;
  rows: Array<{
    enrollmentId: string;
    candidate: { id: string; firstName: string; lastName: string };
    cells: Array<{ lessonId: string; status: string | null }>;
    attendancePct: number;
  }>;
}

function AttendanceTab({ courseId }: { courseId: string }) {
  const qc = useQueryClient();
  const [activeLesson, setActiveLesson] = useState<string | null>(null);
  const { data } = useQuery({
    queryKey: ['attendance-matrix', courseId],
    queryFn: () => api.get<AttendanceMatrix>(`/courses/${courseId}/attendance-matrix`),
  });

  const markCell = useMutation({
    mutationFn: ({ lessonId, enrollmentId, status }: { lessonId: string; enrollmentId: string; status: string }) =>
      api.post(`/courses/lessons/${lessonId}/attendance`, { enrollmentId, status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attendance-matrix', courseId] }),
  });

  const markAllPresent = useMutation({
    mutationFn: (lessonId: string) => {
      const entries = (data?.rows ?? []).map((r) => ({ enrollmentId: r.enrollmentId, status: 'PRESENTE' }));
      return api.post(`/courses/lessons/${lessonId}/attendance-bulk`, { entries });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attendance-matrix', courseId] }),
  });

  if (!data) return <div className="card-bd text-center py-10 mute">Caricamento…</div>;

  // Sfondo cella = stato semantico (verde presente, arancio ritardo, rosso
  // assente). Giustificato non e un allarme: resta neutro viola-soft.
  const statusColor: Record<string, string> = {
    PRESENTE: 'color-mix(in oklab, var(--ok) 18%, var(--surface))',
    RITARDO: 'color-mix(in oklab, var(--warn) 18%, var(--surface))',
    ASSENTE: 'color-mix(in oklab, var(--alert) 16%, var(--surface))',
    GIUSTIFICATO: 'color-mix(in oklab, var(--accent) 12%, var(--surface))',
  };
  const statusGlyph: Record<string, string> = { PRESENTE: '✓', RITARDO: '~', ASSENTE: '✗', GIUSTIFICATO: 'G' };

  const cycle = (current: string | null) => {
    const order = ['PRESENTE', 'RITARDO', 'ASSENTE', 'GIUSTIFICATO'];
    if (!current) return 'PRESENTE';
    const idx = order.indexOf(current);
    return order[(idx + 1) % order.length]!;
  };

  return (
    <div className="space-y-4">
      <div className="filterbar filterbar--float">
        <span className="text-[13px]">Click su una cella per ciclare lo stato: <Chip tone="ok">✓ Presente</Chip> · <Chip tone="warn">~ Ritardo</Chip> · <Chip tone="alert">✗ Assente</Chip> · <Chip>G Giustificato</Chip></span>
        <div className="spacer" />
        {activeLesson && (
          <button className="btn btn-sm" onClick={() => markAllPresent.mutate(activeLesson)}>
            Marca tutti presenti per {data.lessons.find((l) => l.id === activeLesson)?.topic}
          </button>
        )}
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-line">
              <th className="text-left px-3 py-2 sticky left-0 bg-paper-2 z-10" style={{ minWidth: 200 }}>Discente</th>
              {data.lessons.map((l) => (
                <th key={l.id} className="text-center px-1 py-2 cursor-pointer hover:bg-paper-3"
                  onClick={() => setActiveLesson(activeLesson === l.id ? null : l.id)}
                  style={{ background: activeLesson === l.id ? 'var(--paper-3)' : undefined }}>
                  <div className="mono mute text-[12px]">{fmtDateShort(l.date)}</div>
                  <div className="text-[12px] truncate" style={{ maxWidth: 80 }} title={l.topic}>{l.topic}</div>
                </th>
              ))}
              <th className="text-right px-3 py-2"><span className="t-micro">% Pres</span></th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.enrollmentId} className="border-b border-line">
                <td className="px-3 py-2 sticky left-0 bg-white">
                  <div className="flex items-center gap-2">
                    <Avatar initials={`${r.candidate.firstName[0]}${r.candidate.lastName[0]}`} size={22} />
                    <span className="font-semibold">{r.candidate.firstName} {r.candidate.lastName}</span>
                  </div>
                </td>
                {r.cells.map((cell) => (
                  <td key={cell.lessonId} className="text-center px-1 py-1">
                    <button
                      className="w-8 h-8 rounded grid place-items-center font-bold cursor-pointer hover:scale-110 transition"
                      style={{
                        background: cell.status ? statusColor[cell.status] : 'var(--paper-2)',
                        color: cell.status ? 'var(--text)' : 'var(--mute-2)',
                      }}
                      onClick={() => markCell.mutate({ lessonId: cell.lessonId, enrollmentId: r.enrollmentId, status: cycle(cell.status) })}
                    >
                      {cell.status ? statusGlyph[cell.status] : '—'}
                    </button>
                  </td>
                ))}
                <td className="text-right px-3 mono font-semibold">
                  <span className={r.attendancePct >= 80 ? 'text-ok' : r.attendancePct >= 60 ? 'text-warn' : 'text-alert'}>
                    {r.attendancePct}%
                  </span>
                </td>
              </tr>
            ))}
            {!data.rows.length && (
              <tr><td colSpan={data.lessons.length + 2} className="text-center py-10 mute">Nessun iscritto al corso</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =====================================================================
// DISCENTI
// =====================================================================
function StudentsTab({ c }: { c: CourseFull }) {
  return (
    // Tabella full-bleed sul foglio: kicker micro + conteggio + CTA, niente card
    <div className="ed-section">
      <div className="data-hd">
        <span className="t-micro">Discenti iscritti</span>
        <div className="flex items-center gap-3">
          <span className="mono mute text-[12px]">{c.enrollments.length}</span>
          <button className="btn btn-sm" onClick={() => useModalStore.getState().open('addCourseCandidate', { courseId: c.id })}>
            <Icon name="plus" size={12} /> Aggiungi candidato
          </button>
        </div>
      </div>
      <table className="tbl tbl--ed">
        <thead><tr><th>Nome</th><th>Città</th><th>Stato pipeline</th></tr></thead>
        <tbody>
          {c.enrollments.map((e) => (
            <tr key={e.id}>
              <td>
                <div className="row">
                  <Avatar initials={`${e.candidate.firstName[0]}${e.candidate.lastName[0]}`} />
                  <span className="font-semibold">{e.candidate.firstName} {e.candidate.lastName}</span>
                </div>
              </td>
              <td className="mute">{e.candidate.city ?? '—'}</td>
              <td><Chip>{e.candidate.stage.replace(/_/g, ' ').toLowerCase()}</Chip></td>
            </tr>
          ))}
          {!c.enrollments.length && <tr><td colSpan={3} className="text-center py-10 mute text-[13px]">Nessun iscritto</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// =====================================================================
// DOCENTI
// =====================================================================
function TeachersTab({ courseId }: { courseId: string }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState<'user' | 'supplier'>('user');
  const [personId, setPersonId] = useState('');
  const [role, setRole] = useState('Docente');

  const { data } = useQuery({
    queryKey: ['course-teachers', courseId],
    queryFn: () => api.get<Array<{ id: string; role: string; user: any; supplier: any }>>(`/courses/${courseId}/teachers`),
  });
  const { data: users } = useQuery({
    queryKey: ['users-lite'],
    queryFn: () => api.get<{ items: Array<{ id: string; firstName: string; lastName: string; initials: string }> }>('/users?pageSize=200'),
  });
  const { data: suppliers } = useQuery({
    queryKey: ['suppliers-lite'],
    queryFn: () => api.get<{ items: Array<{ id: string; fullName: string }> }>('/suppliers?pageSize=200'),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['course-teachers', courseId] });
    qc.invalidateQueries({ queryKey: ['course', courseId] });
    qc.invalidateQueries({ queryKey: ['talent-teachers'] });
  };
  const add = useMutation({
    mutationFn: () => api.post(`/courses/${courseId}/teachers`, kind === 'user' ? { userId: personId, role } : { supplierId: personId, role }),
    onSuccess: () => { invalidate(); setAdding(false); setPersonId(''); setRole('Docente'); toast.ok('Docente aggiunto'); },
    onError: (e) => toast.alert('Impossibile aggiungere', e instanceof ApiError ? e.message : 'Riprova'),
  });
  const del = useMutation({
    mutationFn: (teacherId: string) => api.delete(`/courses/${courseId}/teachers/${teacherId}`),
    onSuccess: invalidate,
  });

  const teachers = data ?? [];

  return (
    // Tabella full-bleed sul foglio: kicker micro + conteggio + CTA, niente card
    <div className="ed-section">
      <div className="data-hd">
        <span className="t-micro">Docenti assegnati</span>
        <div className="flex items-center gap-3">
          <span className="mono mute text-[12px]">{teachers.length}</span>
          <button className="btn btn-sm btn-primary" onClick={() => setAdding((v) => !v)}>
            <Icon name={adding ? 'x' : 'plus'} size={12} /> {adding ? 'Annulla' : 'Aggiungi docente'}
          </button>
        </div>
      </div>

      {adding && (
        // Il composer resta su card: gli input hanno bisogno del foglio
        <div className="card card-bd mb-4" style={{ background: 'var(--paper-2)' }}>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <div className="field-label">Tipo</div>
              <div className="seg-control">
                <button aria-pressed={kind === 'user'} onClick={() => { setKind('user'); setPersonId(''); }}>Interno</button>
                <button aria-pressed={kind === 'supplier'} onClick={() => { setKind('supplier'); setPersonId(''); }}>Esterno</button>
              </div>
            </div>
            <div className="flex-1" style={{ minWidth: 220 }}>
              <div className="field-label">{kind === 'user' ? 'Utente interno' : 'Fornitore esterno'}</div>
              <select className="inp" value={personId} onChange={(e) => setPersonId(e.target.value)}>
                <option value="">— Seleziona —</option>
                {kind === 'user'
                  ? (users?.items ?? []).map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)
                  : (suppliers?.items ?? []).map((s) => <option key={s.id} value={s.id}>{s.fullName}</option>)}
              </select>
            </div>
            <div style={{ width: 180 }}>
              <div className="field-label">Ruolo</div>
              <input className="inp" value={role} onChange={(e) => setRole(e.target.value)} placeholder="Docente" />
            </div>
            <button className="btn btn-primary" disabled={!personId || add.isPending} onClick={() => add.mutate()}>
              <Icon name="plus" size={12} /> Aggiungi
            </button>
          </div>
          {kind === 'supplier' && (
            <div className="text-[12px] mute mt-2">I docenti esterni sono Fornitori. Se non c'è in lista, crealo prima in <b>Fornitori</b>.</div>
          )}
        </div>
      )}

      <table className="tbl tbl--ed">
        <thead><tr><th>Nome</th><th>Tipo</th><th>Ruolo</th><th className="num">Tariffa</th><th /></tr></thead>
        <tbody>
          {teachers.map((t) => (
            <tr key={t.id}>
              <td>
                <div className="row">
                  <Avatar initials={t.user?.initials ?? t.supplier?.fullName?.slice(0, 2).toUpperCase() ?? '??'} />
                  <span className="font-semibold">{t.user ? `${t.user.firstName} ${t.user.lastName}` : t.supplier?.fullName}</span>
                </div>
              </td>
              <td><span className="chip-label">{t.user ? 'Interno' : 'Esterno'}</span></td>
              <td>{t.role}</td>
              <td className="num mono mute">{t.supplier?.hourlyRate ? `€ ${t.supplier.hourlyRate}/h` : '—'}</td>
              <td className="num">
                <button className="btn btn-sm btn-icon" title="Rimuovi dal corso" onClick={async () => {
                  const name = t.user ? `${t.user.firstName} ${t.user.lastName}` : t.supplier?.fullName;
                  if (await confirmDialog({ title: 'Rimuovi docente', body: `Rimuovere ${name} da questo corso? (non elimina l'anagrafica)`, danger: true, confirmLabel: 'Rimuovi' })) del.mutate(t.id);
                }}>
                  <Icon name="trash" size={12} />
                </button>
              </td>
            </tr>
          ))}
          {!teachers.length && <tr><td colSpan={5} className="text-center py-10 mute text-[13px]">Nessun docente assegnato</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// =====================================================================
// DOCUMENTI
// =====================================================================
function DocumentsTab({ courseId }: { courseId: string }) {
  const qc = useQueryClient();
  const openModal = useModalStore((s) => s.open);

  const { data } = useQuery({
    queryKey: ['course-documents', courseId],
    queryFn: () => api.get<{ items: any[] }>(`/documents?courseId=${courseId}`),
  });
  const del = useMutation({
    mutationFn: (docId: string) => api.delete(`/documents/${docId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['course-documents', courseId] }),
  });

  const docs = data?.items ?? [];

  return (
    // Tabella full-bleed sul foglio: kicker micro + conteggio + CTA, niente card
    <div className="ed-section">
      <div className="data-hd">
        <span className="t-micro">Documenti</span>
        <div className="flex items-center gap-3">
          <span className="mono mute text-[12px]">{docs.length}</span>
          <button className="btn btn-sm btn-primary" onClick={() => openModal('uploadDoc', { scope: 'course', scopeId: courseId })}>
            <Icon name="upload" /> Carica
          </button>
        </div>
      </div>

      <table className="tbl tbl--ed">
          <thead>
            <tr><th style={{ width: 60 }}>Tipo</th><th>Nome</th><th>Versione</th><th>Autore</th><th>Data</th><th className="num">Dim.</th><th style={{ width: 60 }} /></tr>
          </thead>
          <tbody>
            {docs.map((d) => {
              const ext = d.name.split('.').pop()?.toUpperCase() ?? 'FILE';
              return (
                <tr key={d.id}>
                  <td><span className="chip-label font-mono">{ext}</span></td>
                  <td className="font-medium"><Icon name="file" size={12} className="mute inline mr-1.5" />{d.name}</td>
                  <td className="mono mute text-[12px]">{d.version ?? 'v1'}</td>
                  <td>{d.uploader ? <span className="text-[12px]">{d.uploader.firstName[0]}. {d.uploader.lastName}</span> : '—'}</td>
                  <td className="mono mute text-[12px]">{fmtDate(d.createdAt)}</td>
                  <td className="num mono mute text-[12px]">{(d.size / 1024).toFixed(0)} KB</td>
                  <td>
                    <button className="btn btn-sm btn-ghost" onClick={async () => {
                      if (await confirmDialog({ title: 'Elimina documento', body: `Eliminare "${d.name}"?`, danger: true, confirmLabel: 'Elimina' })) del.mutate(d.id);
                    }}>
                      <Icon name="x" size={12} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {!docs.length && (
              <tr><td colSpan={7} style={{ padding: 0 }}>
                <EmptyState
                  icon="file"
                  title="Nessun documento sul corso"
                  description="Contratti, registri e materiali vivono qui."
                  action={(
                    <button className="btn btn-sm mt-3" onClick={() => openModal('uploadDoc', { scope: 'course', scopeId: courseId })}>
                      <Icon name="upload" size={12} /> Carica il primo
                    </button>
                  )}
                />
              </td></tr>
            )}
          </tbody>
        </table>
    </div>
  );
}

// =====================================================================
// VALUTAZIONE
// =====================================================================
function AssessmentTab({ courseId, enrollments }: { courseId: string; enrollments: CourseFull['enrollments'] }) {
  const qc = useQueryClient();
  const openModal = useModalStore((s) => s.open);
  const { data } = useQuery({
    queryKey: ['course-assessments', courseId],
    queryFn: () => api.get<{ items: any[]; byCompetency: any[]; distribution: any; total: number; avg: number }>(`/courses/${courseId}/assessments`),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/courses/assessments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['course-assessments', courseId] }),
  });

  if (!data) return <div className="card-bd text-center py-10 mute">Caricamento…</div>;

  const grades = [
    { k: 'A',  l: '90 – 100', tone: 'ok' as const,    count: data.distribution.A },
    { k: 'B',  l: '75 – 89',  tone: 'ok' as const,    count: data.distribution.B },
    { k: 'C',  l: '60 – 74',  tone: 'warn' as const,  count: data.distribution.C },
    { k: 'D',  l: '50 – 59',  tone: 'warn' as const,  count: data.distribution.D },
    { k: 'NV', l: '< 50',     tone: 'alert' as const, count: data.distribution.NV },
  ];
  const total = data.total || 1;
  const candidateById = new Map(enrollments.map((e) => [e.candidate.id, e.candidate]));
  const idonei = data.distribution.A + data.distribution.B + data.distribution.C;

  return (
    <div className="space-y-6">
      {/* Numeri NUDI sul foglio, separati da hairline: niente scatole né icone */}
      <div className="stat-band">
        <div>
          <div className="st-label">Prove registrate</div>
          <div className="st-value"><AnimatedNumber value={data.total} /></div>
        </div>
        <div>
          <div className="st-label">Voto medio</div>
          <div className="st-value"><AnimatedNumber value={data.avg} format={(n) => n.toFixed(1)} /></div>
          <div className="st-delta">su 100</div>
        </div>
        <div>
          <div className="st-label">Soglia idoneità</div>
          <div className="st-value">60</div>
          <div className="st-delta">punti</div>
        </div>
        <div>
          <div className="st-label">Idonei</div>
          <div className="st-value"><AnimatedNumber value={idonei} /></div>
          <div className="st-delta">{data.total > 0 ? Math.round((idonei / data.total) * 100) : 0}% delle prove</div>
        </div>
      </div>

      {/* Distribuzione voti: tabella full-bleed sul foglio (il voto è uno
          stato reale → pill semantica; conteggi piatti in mono) */}
      <div className="ed-section">
        <div className="data-hd">
          <span className="t-micro">Distribuzione voti</span>
          <span className="mono mute text-[12px]">{data.total} prove</span>
        </div>
        <table className="tbl tbl--ed">
          <thead><tr><th>Voto</th><th>Fascia</th><th>Quota</th><th className="num">Prove</th><th className="num">%</th></tr></thead>
          <tbody>
            {grades.map((g) => {
              const pct = total > 0 ? ((g.count ?? 0) / total) * 100 : 0;
              return (
                <tr key={g.k}>
                  <td><Chip tone={g.tone}>{g.k}</Chip></td>
                  <td className="mono mute text-[12px]">{g.l}</td>
                  <td>
                    <div className="barbar" style={{ width: 160 }}>
                      <span style={{ width: `${pct}%` }} />
                    </div>
                  </td>
                  <td className="num mono">{g.count ?? 0}</td>
                  <td className="num mono mute">{pct.toFixed(0)}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="ed-section">
        <div className="data-hd">
          <span className="t-micro">Prove erogate</span>
          <div className="flex items-center gap-3">
            <span className="mono mute text-[12px]">{data.items.length}</span>
            <button className="btn btn-sm btn-primary" onClick={() => openModal('newAssessment', { courseId })}>
              <Icon name="plus" size={12} /> Registra valutazione
            </button>
          </div>
        </div>
        <table className="tbl tbl--ed">
          <thead><tr><th>Discente</th><th>Prova</th><th className="num">Punteggio</th><th>Voto</th><th style={{ width: 50 }} /></tr></thead>
          <tbody>
            {data.items.map((a) => {
              const cand = candidateById.get(a.candidateId);
              const tone = a.score >= 75 ? 'ok' : a.score >= 60 ? 'warn' : 'alert';
              return (
                <tr key={a.id}>
                  <td>{cand ? `${cand.firstName} ${cand.lastName}` : '—'}</td>
                  <td>{a.competency}</td>
                  <td className="num mono font-semibold">{a.score}</td>
                  <td><Chip tone={tone}>{a.grade ?? '—'}</Chip></td>
                  <td>
                    <button className="btn btn-sm btn-ghost" onClick={async () => {
                      if (await confirmDialog({ title: 'Elimina valutazione', body: 'Eliminare questa valutazione?', danger: true, confirmLabel: 'Elimina' })) del.mutate(a.id);
                    }}>
                      <Icon name="x" size={12} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {!data.items.length && (
              <tr><td colSpan={5} style={{ padding: 0 }}>
                <EmptyState
                  icon="chart"
                  title="Nessuna valutazione registrata"
                  description="Le prove e i punteggi dei discenti compaiono qui."
                  action={(
                    <button className="btn btn-sm mt-3" onClick={() => openModal('newAssessment', { courseId })}>
                      <Icon name="plus" size={12} /> Registra la prima
                    </button>
                  )}
                />
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =====================================================================
// PLACEMENT (Formatemp)
// =====================================================================
function PlacementTab({ courseId }: { courseId: string }) {
  const { data } = useQuery({
    queryKey: ['course-placement', courseId],
    queryFn: () => api.get<{ rows: any[]; totals: { total: number; assunti: number; somministrati: number; inCorso: number; senzaPlacement: number } }>(`/courses/${courseId}/placement-stats`),
  });

  if (!data) return <div className="card-bd text-center py-10 mute">Caricamento…</div>;

  const placementRate = data.totals.total > 0
    ? Math.round(((data.totals.assunti + data.totals.somministrati) / data.totals.total) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Numeri NUDI sul foglio, separati da hairline: niente scatole né icone */}
      <div className="stat-band">
        <div>
          <div className="st-label">Discenti totali</div>
          <div className="st-value"><AnimatedNumber value={data.totals.total} /></div>
        </div>
        <div>
          <div className="st-label">Placement attivi</div>
          <div className="st-value"><AnimatedNumber value={data.totals.assunti + data.totals.somministrati} /></div>
          <div className="st-delta">{placementRate}% dei discenti</div>
        </div>
        <div>
          <div className="st-label">Assunti</div>
          <div className="st-value"><AnimatedNumber value={data.totals.assunti} /></div>
        </div>
        <div>
          <div className="st-label">In corso</div>
          <div className="st-value"><AnimatedNumber value={data.totals.inCorso} /></div>
          <div className="st-delta">proposte + colloqui</div>
        </div>
      </div>

      {/* Tabella full-bleed sul foglio: kicker micro + conteggio mono */}
      <div className="ed-section">
        <div className="data-hd">
          <span className="t-micro">Stato placement per discente</span>
          <span className="mono mute text-[12px]">{data.rows.length}</span>
        </div>
        <table className="tbl tbl--ed">
          <thead><tr><th>Discente</th><th>Stage pipeline</th><th>Azienda</th><th>Posizione</th><th>Start</th><th>Stato placement</th></tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.enrollmentId}>
                <td>
                  <div className="row">
                    <Avatar initials={`${r.candidate.firstName[0]}${r.candidate.lastName[0]}`} />
                    <span className="font-semibold">{r.candidate.firstName} {r.candidate.lastName}</span>
                  </div>
                </td>
                <td><Chip>{r.candidate.stage.replace(/_/g, ' ').toLowerCase()}</Chip></td>
                <td>{r.placement?.client?.legalName ?? '—'}</td>
                <td>{r.placement?.position ?? '—'}</td>
                <td className="mono">{fmtDate(r.placement?.startDate)}</td>
                <td>{r.placement
                  ? <Chip tone={statusTone(r.placement.status)} dot>{r.placement.status.replace(/_/g, ' ')}</Chip>
                  : <Chip tone="ghost">Nessuno</Chip>}</td>
              </tr>
            ))}
            {!data.rows.length && <tr><td colSpan={6} className="text-center py-10 mute text-[13px]">Nessun discente</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =====================================================================
// RENDICONTAZIONE (Fondimpresa)
// =====================================================================
function ReportingTab({ courseId }: { courseId: string }) {
  const qc = useQueryClient();
  const openModal = useModalStore((s) => s.open);
  const { data } = useQuery({
    queryKey: ['course-reporting', courseId],
    queryFn: () => api.get<{ checklist: any[]; uploadedCount: number; totalCount: number; readiness: number; reporting: any }>(`/courses/${courseId}/reporting`),
  });
  const submit = useMutation({
    mutationFn: (notes: string) => api.post(`/courses/${courseId}/reporting/submit`, { notes }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['course-reporting', courseId] }),
  });

  if (!data) return <div className="card-bd text-center py-10 mute">Caricamento…</div>;

  const tone = data.readiness >= 80 ? 'ok' : data.readiness >= 50 ? 'warn' : 'alert';

  return (
    <div className="space-y-6">
      {/* Numeri NUDI sul foglio, separati da hairline: niente scatole né icone */}
      <div className="stat-band">
        <div>
          <div className="st-label">Documenti caricati</div>
          <div className="st-value"><AnimatedNumber value={data.uploadedCount} /><span className="u">/{data.totalCount}</span></div>
        </div>
        <div>
          <div className="st-label">Readiness</div>
          <div className="st-value"><AnimatedNumber value={data.readiness} /><span className="u">%</span></div>
          <div className="st-delta">{data.readiness >= 80 ? 'pronto' : 'in lavorazione'}</div>
        </div>
        <div>
          <div className="st-label">Rendiconto</div>
          <div className="st-value">{data.reporting?.submitted ? 'Inviato' : 'Da inviare'}</div>
          <div className="st-delta">{data.reporting?.submittedAt ? fmtDate(data.reporting.submittedAt) : '—'}</div>
        </div>
      </div>

      {/* Checklist: kicker-block nudo sul foglio, righe separate da hairline */}
      <div>
        <div className="data-hd">
          <span className="t-micro">Checklist documenti Fondimpresa</span>
          <Chip tone={tone}>{data.uploadedCount}/{data.totalCount} caricati</Chip>
        </div>
        <div>
          {data.checklist.map((c: any) => (
            <div key={c.key} className="flex items-center gap-3 py-2.5 border-b border-line last:border-0">
              <div className="w-6 h-6 rounded-full grid place-items-center"
                style={{ background: c.uploaded ? 'color-mix(in oklab, var(--ok) 18%, var(--surface))' : 'var(--paper-2)', color: c.uploaded ? 'var(--ok)' : 'var(--mute-2)' }}>
                {c.uploaded ? <Icon name="check" size={12} /> : <Icon name="file" size={12} />}
              </div>
              <div className="flex-1 text-[13px] font-medium">{c.label}</div>
              {c.uploaded ? (
                <Chip tone="ok">caricato</Chip>
              ) : (
                <button className="btn btn-sm" onClick={() => openModal('uploadDoc', { scope: 'course', scopeId: courseId })}>
                  <Icon name="upload" size={10} /> Carica
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Invio: kicker-block nudo, l'azione resta primaria */}
      <div>
        <div className="data-hd">
          <span className="t-micro">Invio rendiconto</span>
        </div>
        {data.reporting?.submitted ? (
          <div className=" flex items-center gap-2 text-ok">
            <Icon name="check" size={14} /> Rendiconto inviato il {fmtDate(data.reporting.submittedAt)}
          </div>
        ) : (
          <button className="btn btn-primary" onClick={() => submit.mutate('')} disabled={data.readiness < 80 || submit.isPending}>
            <Icon name="send" size={12} /> Invia rendiconto a Fondimpresa
          </button>
        )}
        {data.readiness < 80 && !data.reporting?.submitted && (
          <div className="mute text-[13px] mt-2">Readiness richiesta: 80%. Carica i documenti mancanti per abilitare l'invio.</div>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// BUDGET
// =====================================================================
function BudgetTab({ courseId, showMargin }: { courseId: string; showMargin: boolean }) {
  const { data } = useQuery({
    queryKey: ['course-budget', courseId],
    queryFn: () => api.get<any>(`/courses/${courseId}/budget`),
  });

  if (!data) return <div className="card-bd text-center py-10 mute">Caricamento…</div>;

  return (
    <div className="space-y-6">
      {/* Numeri NUDI sul foglio; costi e margine restano gated (••• Riservato) */}
      <div className="stat-band">
        <div>
          <div className="st-label">Ricavi</div>
          <div className="st-value"><span className="u">€</span><AnimatedNumber value={Number(data.revenue) || 0} format={fmtEur} /></div>
        </div>
        {showMargin ? (
          <div>
            <div className="st-label">Costi</div>
            <div className="st-value"><span className="u">€</span><AnimatedNumber value={Number(data.cost) || 0} format={fmtEur} /></div>
          </div>
        ) : (
          <div>
            <div className="st-label">Costi</div>
            <div className="st-value mute-2">•••</div>
            <div className="st-delta">Riservato</div>
          </div>
        )}
        {showMargin ? (
          <div>
            <div className="st-label">Margine</div>
            <div className="st-value"><span className="u">€</span><AnimatedNumber value={Number(data.marginAbs) || 0} format={fmtEur} /></div>
            <div className="st-delta">{data.marginPct?.toFixed(1)}%</div>
          </div>
        ) : (
          <div>
            <div className="st-label">Margine</div>
            <div className="st-value mute-2">•••</div>
            <div className="st-delta">Riservato</div>
          </div>
        )}
        <div>
          <div className="st-label">Ore corso</div>
          <div className="st-value"><AnimatedNumber value={Number(data.hoursTotal) || 0} /></div>
          <div className="st-delta">{data.lessonsCount} lezioni</div>
        </div>
      </div>

      {showMargin && data.breakdown && (
        <div className="ed-section">
          <div className="data-hd">
            <span className="t-micro">Ripartizione costi</span>
          </div>
          <table className="tbl tbl--ed">
            <thead><tr><th>Categoria</th><th>Ripartizione</th><th className="num">Importo</th><th className="num">%</th></tr></thead>
            <tbody>
              {data.breakdown.map((b: any) => (
                <tr key={b.category}>
                  <td className="font-medium">{b.category}</td>
                  <td>
                    <div className="barbar" style={{ width: 200 }}>
                      <span style={{ width: `${b.pct}%` }} />
                    </div>
                  </td>
                  <td className="num mono">€ {fmtEur(b.amount)}</td>
                  <td className="num mono mute">{b.pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// ATTIVITÀ
// =====================================================================
function ActivityTab({ courseId }: { courseId: string }) {
  const { data } = useQuery({
    queryKey: ['course-activity', courseId],
    queryFn: () => api.get<Array<{ kind: string; at: string; message: string }>>(`/courses/${courseId}/activity`),
  });
  return (
    // Log: kicker-block nudo sul foglio, niente card
    <div>
      <div className="data-hd">
        <span className="t-micro">Log attività corso</span>
        <span className="mono mute text-[12px]">{(data ?? []).length}</span>
      </div>
      <div className="py-1">
        {(data ?? []).map((a, i) => (
          <div key={i} className="activity-row">
            <div className={`activity-dot ${a.kind === 'lesson' ? 'activity-task' : 'activity-comm'}`}>
              <Icon name={a.kind === 'lesson' ? 'check' : 'plus'} size={10} />
            </div>
            <div className="flex-1">
              <div className="">{a.message}</div>
              <div className="mute mono text-[12px] mt-0.5">{fmtDate(a.at)}</div>
            </div>
          </div>
        ))}
        {!data?.length && <div className="px-4 py-12 text-center text-[13px] mute">Nessuna attività registrata</div>}
      </div>
    </div>
  );
}

// =====================================================================
// PIPELINE RECRUITING (dentro al corso) — kanban per-corso
// =====================================================================
const COURSE_PIPELINE: CandidateStage[] = [
  CandidateStage.NUOVO_CANDIDATO, CandidateStage.SCREENING, CandidateStage.IDONEO,
  CandidateStage.ISCRITTO_AL_CORSO, CandidateStage.FREQUENTANTE, CandidateStage.PRONTO_PER_PLACEMENT,
  CandidateStage.PROPOSTO_AD_AZIENDA, CandidateStage.COLLOQUIO_FISSATO, CandidateStage.SOMMINISTRATO,
  CandidateStage.ASSUNTO, CandidateStage.SCARTATO,
];

interface PipelineEnrollment {
  id: string; stage: CandidateStage; order: number;
  candidate: { id: string; firstName: string; lastName: string; skill: string | null; seniority: string | null; city: string | null };
}

function CoursePipelineTab({ courseId }: { courseId: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const openModal = useModalStore((s) => s.open);
  const key = ['course-pipeline', courseId];

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api.get<{ enrollments: PipelineEnrollment[] }>(`/courses/${courseId}/pipeline`),
  });
  const enrollments = data?.enrollments ?? [];

  const move = useMutation({
    mutationFn: ({ id, stage, order }: { id: string; stage: CandidateStage; order: number }) =>
      api.patch(`/courses/${courseId}/enrollments/${id}/move`, { stage, order }),
    onMutate: async ({ id, stage, order }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<{ enrollments: PipelineEnrollment[] }>(key);
      if (prev) {
        const moving = prev.enrollments.find((e) => e.id === id);
        if (moving) {
          const others = prev.enrollments.filter((e) => e.id !== id);
          const sameCol = others.filter((e) => e.stage === stage);
          const otherCols = others.filter((e) => e.stage !== stage);
          const pos = Math.max(0, Math.min(order, sameCol.length));
          const updated = { ...moving, stage };
          const newSame = [...sameCol.slice(0, pos), updated, ...sameCol.slice(pos)];
          qc.setQueryData(key, { enrollments: [...otherCols, ...newSame] });
        }
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/courses/${courseId}/enrollments/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ['course', courseId] }); },
  });

  const dnd = useKanbanDnd<CandidateStage>((id, stage, position) => move.mutate({ id, stage, order: position }));
  const anyDragging = dnd.isAnyDragging();

  return (
    <div className="space-y-4">
      <div className="row between">
        <div className="mute text-[13px]">
          {enrollments.length} candidati nel funnel · trascina le card per cambiare fase. La fase aggiorna anche lo stato globale del candidato.
        </div>
        <button className="btn btn-sm btn-primary" onClick={() => openModal('addCourseCandidate', { courseId })}>
          <Icon name="plus" size={12} /> Aggiungi candidato
        </button>
      </div>

      {isLoading ? (
        <div className="card-bd text-center py-10 mute">Caricamento…</div>
      ) : (
        <div className="kanban" style={{ gridAutoColumns: 230 }}>
          {COURSE_PIPELINE.map((stage) => {
            const cards = enrollments.filter((e) => e.stage === stage);
            const isOver = dnd.isOverColumn(stage);
            return (
              <div className={`kanban-col${isOver ? ' is-over' : ''}`} key={stage} {...dnd.columnProps(stage)}>
                <div className="kanban-col-hd">
                  <div className="kanban-col-title">
                    <span className="dot" style={{
                      background: ['ASSUNTO', 'SOMMINISTRATO', 'PRONTO_PER_PLACEMENT', 'IDONEO'].includes(stage) ? 'var(--ok)'
                        : stage === 'SCARTATO' ? 'var(--alert)'
                        : ['FREQUENTANTE', 'ISCRITTO_AL_CORSO', 'COLLOQUIO_FISSATO'].includes(stage) ? 'var(--warn)'
                        : 'var(--mute)' }} />
                    <span className="text-[12px]">{stage.replace(/_/g, ' ').toLowerCase()}</span>
                  </div>
                  <span className="kanban-col-count">{cards.length}</span>
                </div>
                <div className="kanban-col-body">
                  <KanbanDropZone dnd={dnd} status={stage} position={0} visible={anyDragging} />
                  {cards.map((e, idx) => (
                    <Fragment key={e.id}>
                      <div className="kanban-card cursor-grab active:cursor-grabbing group" {...dnd.cardProps(e.id, e.stage)}>
                        <div className="flex gap-2 items-center mb-1">
                          <Avatar initials={`${e.candidate.firstName[0]}${e.candidate.lastName[0]}`} size={22} />
                          <div className="kanban-card-title m-0 flex-1" onClick={() => navigate(`/candidates/${e.candidate.id}`)}>
                            {e.candidate.firstName} {e.candidate.lastName}
                          </div>
                          <button className="opacity-0 group-hover:opacity-100 transition-opacity" title="Rimuovi dal corso"
                            onClick={async (ev) => {
                              ev.stopPropagation();
                              if (await confirmDialog({ title: 'Rimuovi dal corso', body: `Rimuovere ${e.candidate.firstName} ${e.candidate.lastName} dal funnel?`, danger: true, confirmLabel: 'Rimuovi' })) del.mutate(e.id);
                            }}>
                            <Icon name="trash" size={12} className="mute" />
                          </button>
                        </div>
                        <div className="mute text-[12px]">{[e.candidate.skill, e.candidate.seniority, e.candidate.city].filter(Boolean).join(' · ') || '—'}</div>
                      </div>
                      <KanbanDropZone dnd={dnd} status={stage} position={idx + 1} visible={anyDragging} />
                    </Fragment>
                  ))}
                  {!cards.length && !anyDragging && <div className="text-center py-4 text-[12px] mute-2">—</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function KanbanDropZone<S extends string>({ dnd, status, position, visible }: {
  dnd: ReturnType<typeof useKanbanDnd<S>>;
  status: S;
  position: number;
  visible: boolean;
}) {
  const active = dnd.isOverZone(status, position);
  if (!visible) return null;
  return <div {...dnd.dropZoneProps(status, position)} className={`kanban-dropzone${active ? ' is-over' : ''}`} />;
}
